import { ToggleGroup } from "@kobalte/core/toggle-group"
import { buildArticleUrl } from "@repo/fika-blog/src/blogUrl.js"
import { getLanguageLabel, type Locale } from "@repo/fika-shared/src/locale"
import {
  BLUESKY,
  createSocialShare,
  EMAIL,
  FACEBOOK,
  LINKEDIN,
  PINTEREST,
  REDDIT,
  TELEGRAM,
  WHATSAPP,
  X,
} from "@solid-primitives/share"
import {
  siBluesky,
  siFacebook,
  siGmail,
  siPinterest,
  siReddit,
  siTelegram,
  siWhatsapp,
  siX,
} from "simple-icons"
import {
  type Accessor,
  createEffect,
  createMemo,
  createSelector,
  createSignal,
  For,
  type Setter,
  Show,
} from "solid-js"
import { useApplication } from "@/context/session-provider"
import { uploadImage } from "@/data/upload-image"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import {
  buttonToggleClass,
  buttonToggleGroupClass,
} from "@/designSystem/components/mini/mini.css"
import { Text } from "@/designSystem/components/text"
import { TextField } from "@/designSystem/components/text-field"
import { Tooltip } from "@/designSystem/components/tooltip"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { getTitle } from "@/lib/get-title"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"
import type { EditorPost } from "../post-editor-context"
import { PostEditorFragment } from "../post-editor-context/provider"
import {
  emptyImageClass,
  imageActionsRowClass,
  imageAreaClass,
  linkContainerClass,
  linkTextClass,
  previewCardClass,
  previewDescriptionClass,
  previewImageClass,
  previewTitleClass,
  resetImageButtonClass,
  socialShareButtonTooltipTriggerClass,
  socialShareGridClass,
  uploadImageButtonClass,
} from "./share-content.css"
import { SocialMediaImageGenerator } from "./social-image-generator"

const FIKA_PUBLIC_URL = import.meta.env.VITE_FIKA_PUBLIC_URL

export function ShareContent(props: { post: EditorPost; userName: string }) {
  const { LL } = useI18nContext()

  const mainVersion = createMemo(() => {
    const mainLocale = props.post.mainLocale
    return props.post.publishedVersions?.find(v => v.locale === mainLocale)
  })

  const enabledLocales = createMemo(() => {
    return (
      props.post.availableLocales?.filter(l => l !== props.post.mainLocale) ??
      []
    )
  })

  const [selectedLocale, setSelectedLocale] = createSignal<Locale>(
    props.post.mainLocale as Locale,
  )
  const [isEditing, setIsEditing] = createSignal(false)
  const [isUploadingCoverImage, setIsUploadingCoverImage] = createSignal(false)
  let coverImageInputRef: HTMLInputElement | undefined

  const selectedVersion = createMemo(() => {
    const locale = selectedLocale()
    if (!locale) return null

    return props.post.publishedVersions?.find(v => v.locale === locale)
  })

  const defaultMetaTitle = createMemo(() => {
    const locale = selectedLocale()
    if (!locale || locale === props.post.mainLocale) {
      const main = mainVersion()
      return main?.metaTitle || main?.title || ""
    }
    const version = selectedVersion()
    return version?.metaTitle || version?.title || ""
  })

  const defaultMetaDescription = createMemo(() => {
    const locale = selectedLocale()
    if (!locale || locale === props.post.mainLocale) {
      const main = mainVersion()
      return main?.metaDescription || main?.description || ""
    }
    const version = selectedVersion()
    return version?.metaDescription || version?.description || ""
  })

  const defaultImageUrl = createMemo(() => {
    const version = selectedVersion()
    return version?.socialImageUrl || version?.imageHeaderUrl || ""
  })

  const socialImageUrl = createMemo(
    () => selectedVersion()?.socialImageUrl ?? null,
  )

  const isCustomImage = () => !!socialImageUrl()

  const publicUrl = createMemo(() => {
    const locale = selectedLocale()
    if (!locale) return ""

    const version =
      locale === props.post.mainLocale ? mainVersion() : selectedVersion()
    if (!version) return ""

    return buildArticleUrl({
      publicUrl: FIKA_PUBLIC_URL,
      userName: props.userName,
      postMainLocale: props.post.mainLocale,
      locale,
      title: getTitle(version.title),
      postId: props.post.id,
    })
  })

  const [formTitle, setFormTitle] = createSignal(defaultMetaTitle())
  const [formDescription, setFormDescription] = createSignal(
    defaultMetaDescription(),
  )

  createEffect(() => {
    if (!isEditing()) {
      setFormTitle(defaultMetaTitle())
      setFormDescription(defaultMetaDescription())
    }
  })

  const seoMetadataDirty = createMemo(
    () =>
      formTitle() !== defaultMetaTitle() ||
      formDescription() !== defaultMetaDescription(),
  )

  const SavePostMutation = graphql(
    `mutation SavePostForShare($id: ID!, $locale: Locale, $input: SavePostInput!) {
      savePost(id: $id, locale: $locale, input: $input) {
        ... on MutationSavePostSuccess {
          data {
            ...PostEditorFragment
          }
        }
        ...ErrorFragment
      }
    }`,
    [PostEditorFragment, ErrorFragment],
  )
  const [savePostState, savePost] = createMutationWithErrorHandling(
    SavePostMutation,
    "savePost",
  )

  async function onSubmitSeoMetadata() {
    const locale = selectedLocale()

    if (!locale) return

    try {
      const version = selectedVersion()
      if (version) {
        await savePost({
          id: props.post.id,
          locale,
          input: {
            title: version.title || "",
            description: version.description || "",
            content: version.content || "",
            imageHeaderUrl: version.imageHeaderUrl || "",
            socialImageUrl: version.socialImageUrl || null,
            metaTitle: formTitle() || null,
            metaDescription: formDescription() || null,
          },
        })
      }
      setIsEditing(false)
    } catch {
      toast.error(LL().common.error())
    }
  }

  function handleCancel() {
    setFormTitle(defaultMetaTitle())
    setFormDescription(defaultMetaDescription())
    setIsEditing(false)
  }

  async function handleCoverImageFileSelected(e: Event) {
    const input = e.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ""
    if (!file) return

    const locale = selectedLocale()
    if (!locale) return

    setIsUploadingCoverImage(true)
    try {
      const url = await uploadImage(file, ["post", props.post.id])
      const version = selectedVersion()
      if (version) {
        await savePost({
          id: props.post.id,
          locale,
          input: {
            title: version.title || "",
            description: version.description || "",
            content: version.content || "",
            imageHeaderUrl: version.imageHeaderUrl || "",
            socialImageUrl: url,
            metaTitle: version.metaTitle || null,
            metaDescription: version.metaDescription || null,
          },
        })
      }
      toast.success(LL().postShare.coverImageUpdated())
    } catch {
      toast.error(LL().common.error())
    } finally {
      setIsUploadingCoverImage(false)
    }
  }

  async function handleResetCoverImage() {
    const locale = selectedLocale()
    if (!locale) return
    setIsUploadingCoverImage(true)
    try {
      const version = selectedVersion()
      if (version) {
        await savePost({
          id: props.post.id,
          locale,
          input: {
            title: version.title || "",
            description: version.description || "",
            content: version.content || "",
            imageHeaderUrl: version.imageHeaderUrl || "",
            socialImageUrl: null,
            metaTitle: version.metaTitle || null,
            metaDescription: version.metaDescription || null,
          },
        })
      }
    } catch {
      toast.error(LL().common.error())
    } finally {
      setIsUploadingCoverImage(false)
    }
  }

  return (
    <Box gap="16">
      <LanguageToggle
        mainLocale={props.post.mainLocale}
        enabledLocales={enabledLocales()}
        selectedLocale={selectedLocale}
        setSelectedLocale={setSelectedLocale}
      />

      <input
        ref={el => {
          coverImageInputRef = el
        }}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleCoverImageFileSelected}
      />

      <PreviewCard
        imageUrl={defaultImageUrl()}
        title={formTitle}
        setTitle={setFormTitle}
        description={formDescription}
        setDescription={setFormDescription}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        isDirty={seoMetadataDirty()}
        isSaving={savePostState.fetching}
        onSubmit={onSubmitSeoMetadata}
        onCancel={handleCancel}
        onUploadImage={() => coverImageInputRef?.click()}
        isUploadingImage={isUploadingCoverImage()}
        isCustomImage={isCustomImage()}
        onResetImage={handleResetCoverImage}
      />

      <ArticleLink url={publicUrl()} />

      <Show when={selectedVersion()} keyed>
        {version => (
          <SocialMediaImageGenerator
            socialImageUrls={version.socialImageUrls}
          />
        )}
      </Show>

      <SocialShareSection
        post={props.post}
        selectedLocale={selectedLocale}
        publicUrl={publicUrl()}
      />
    </Box>
  )
}

function LanguageToggleItem(props: {
  locale: Locale
  isSelected: (value: Locale) => boolean
}) {
  const selected = () => props.isSelected(props.locale)

  return (
    <ToggleGroup.Item
      class={buttonToggleClass}
      aria-label={getLanguageLabel(props.locale)}
      value={props.locale}
      data-selected={selected()}
    >
      <span>{getLanguageLabel(props.locale)}</span>
    </ToggleGroup.Item>
  )
}

function LanguageToggle(props: {
  mainLocale: Locale
  enabledLocales: Locale[]
  selectedLocale: Accessor<Locale>
  setSelectedLocale: Setter<Locale>
}) {
  const { LL } = useI18nContext()

  const allLocales = createMemo(() => [
    props.mainLocale,
    ...props.enabledLocales,
  ])

  const hasTranslations = createMemo(() => props.enabledLocales.length > 0)

  const isSelected = createSelector(props.selectedLocale)

  return (
    <Box gap="8">
      <Text fontSize="s" color="sand700" fontWeight="500">
        {LL().postShare.preview()}
      </Text>
      <Text fontSize="xs" color="sand600">
        {LL().postShare.previewDescription()}
      </Text>
      <Show when={hasTranslations()}>
        <ToggleGroup
          class={buttonToggleGroupClass}
          value={props.selectedLocale()}
          onChange={props.setSelectedLocale}
        >
          <For each={allLocales()}>
            {locale => (
              <LanguageToggleItem locale={locale} isSelected={isSelected} />
            )}
          </For>
        </ToggleGroup>
      </Show>
    </Box>
  )
}

function PreviewCard(props: {
  imageUrl: string
  title: Accessor<string>
  setTitle: Setter<string>
  description: Accessor<string>
  setDescription: Setter<string>
  isEditing: Accessor<boolean>
  setIsEditing: Setter<boolean>
  isDirty: boolean
  isSaving: boolean
  onSubmit: () => void
  onCancel: () => void
  onUploadImage: () => void
  isUploadingImage: boolean
  isCustomImage: boolean
  onResetImage: () => void
}) {
  const { LL } = useI18nContext()

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        if (props.isEditing()) {
          props.onSubmit()
        }
      }}
      class={previewCardClass}
    >
      <div class={imageAreaClass}>
        <Show
          when={props.imageUrl}
          fallback={
            <div class={emptyImageClass}>
              <Icon name="image" size="32" color="sand400" />
            </div>
          }
        >
          {url => <img src={url()} alt="" class={previewImageClass} />}
        </Show>
        <div class={imageActionsRowClass}>
          <Show when={props.isCustomImage}>
            <button
              type="button"
              class={resetImageButtonClass}
              onClick={props.onResetImage}
              disabled={props.isUploadingImage}
              title={LL().postShare.resetCoverImage()}
            >
              <Icon name="undo-2" size="14" color="sand700" />
            </button>
          </Show>
          <button
            type="button"
            class={uploadImageButtonClass}
            onClick={props.onUploadImage}
            disabled={props.isUploadingImage}
            title={LL().postShare.setCoverImage()}
          >
            <Icon name="upload-cloud" size="14" color="sand700" />
            {LL().postShare.setCoverImage()}
          </button>
        </div>
      </div>

      <Show
        when={props.isEditing()}
        fallback={
          <>
            <div class={previewTitleClass}>{props.title()}</div>
            <Show when={props.description()}>
              {desc => <div class={previewDescriptionClass}>{desc()}</div>}
            </Show>
          </>
        }
      >
        <TextField value={props.title()} onChange={props.setTitle}>
          <TextField.Label>{LL().postShare.metaTitleLabel()}</TextField.Label>
          <TextField.TextArea
            rows="1"
            autoResize
            placeholder={LL().postShare.metaTitlePlaceholder()}
          />
        </TextField>
        <TextField value={props.description()} onChange={props.setDescription}>
          <TextField.Label>
            {LL().postShare.metaDescriptionLabel()}
          </TextField.Label>
          <TextField.TextArea
            rows="3"
            autoResize
            placeholder={LL().postShare.metaDescriptionPlaceholder()}
          />
        </TextField>
        <Box flexDirection="row" justifyContent="flex-end" gap="8">
          <Button
            type="button"
            kind="surface"
            hue="sand"
            size="s"
            onClick={props.onCancel}
            disabled={props.isSaving}
          >
            {LL().notes.cancel()}
          </Button>
          <Button
            type="submit"
            hue="emerald"
            size="s"
            disabled={!props.isDirty}
            active={props.isSaving}
          >
            {LL().common.save()}
          </Button>
        </Box>
      </Show>
      <Show when={!props.isEditing()}>
        <Box flexDirection="row" justifyContent="flex-end">
          <Button
            type="button"
            kind="surface"
            hue="sand"
            size="s"
            onClick={() => props.setIsEditing(true)}
          >
            {LL().common.edit()}
          </Button>
        </Box>
      </Show>
    </form>
  )
}

function ArticleLink(props: { url: string }) {
  const { LL } = useI18nContext()
  const [copied, setCopied] = createSignal(false)

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(props.url)
      setCopied(true)
      toast.success(LL().postShare.linkCopied())
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(LL().common.error())
    }
  }

  return (
    <Box gap="8">
      <Text fontSize="s" color="sand700" fontWeight="500">
        {LL().postShare.articleLink()}
      </Text>
      <div class={linkContainerClass}>
        <a
          href={props.url}
          target="_blank"
          rel="noopener noreferrer"
          class={linkTextClass}
        >
          {props.url}
        </a>
        <Button
          kind="surface"
          hue="sand"
          size="s"
          onClick={copyToClipboard}
          title={LL().postShare.copyLink()}
        >
          <Icon
            name={copied() ? "check" : "clipboard-list"}
            size="18"
            color={copied() ? "emerald700" : "amber700"}
          />
        </Button>
      </div>
    </Box>
  )
}

const linkedinPath =
  "M62,62 L51.315625,62 L51.315625,43.8021149 C51.315625,38.8127542 49.4197917,36.0245323 45.4707031,36.0245323 C41.1746094,36.0245323 38.9300781,38.9261103 38.9300781,43.8021149 L38.9300781,62 L28.6333333,62 L28.6333333,27.3333333 L38.9300781,27.3333333 L38.9300781,32.0029283 C38.9300781,32.0029283 42.0260417,26.2742151 49.3825521,26.2742151 C56.7356771,26.2742151 62,30.7644705 62,40.051212 L62,62 Z M16.349349,22.7940133 C12.8420573,22.7940133 10,19.9296567 10,16.3970067 C10,12.8643566 12.8420573,10 16.349349,10 C19.8566406,10 22.6970052,12.8643566 22.6970052,16.3970067 C22.6970052,19.9296567 19.8566406,22.7940133 16.349349,22.7940133 Z M11.0325521,62 L21.769401,62 L21.769401,27.3333333 L11.0325521,27.3333333 L11.0325521,62 Z"
const linkedinViewBox = "0 0 72 72"

const socialIcons = [
  {
    network: LINKEDIN,
    title: "LinkedIn",
    path: linkedinPath,
    viewBox: linkedinViewBox,
  },
  { network: X, title: "X", path: siX.path },
  { network: FACEBOOK, title: "Facebook", path: siFacebook.path },
  { network: BLUESKY, title: "Bluesky", path: siBluesky.path },
  { network: REDDIT, title: "Reddit", path: siReddit.path },
  { network: PINTEREST, title: "Pinterest", path: siPinterest.path },
  { network: WHATSAPP, title: "WhatsApp", path: siWhatsapp.path },
  { network: TELEGRAM, title: "Telegram", path: siTelegram.path },
  { network: EMAIL, title: "Email", path: siGmail.path },
] as const

type SocialIconConfig = (typeof socialIcons)[number]

function SocialShareButton(props: {
  config: SocialIconConfig
  url: string
  title: string
  description: string
  media: string
  hashtags: string
}) {
  const { LL } = useI18nContext()

  const [share] = createSocialShare(() => ({
    url: props.url,
    title: props.title,
    description: props.description,
    media: props.media,
    hashtags: props.hashtags,
  }))

  function handleClick() {
    share(props.config.network)
  }

  const shareLabel = () =>
    LL().postShare.shareOn({ network: props.config.title })

  return (
    <Tooltip
      ignoreSafeArea={true}
      description={shareLabel()}
      tooltipTriggerClass={socialShareButtonTooltipTriggerClass}
    >
      <Button
        hue="sand"
        kind="surface"
        size="m"
        onClick={handleClick}
        aria-label={shareLabel()}
      >
        <svg
          role="img"
          viewBox={
            "viewBox" in props.config ? props.config.viewBox : "0 0 24 24"
          }
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          width="20"
          height="20"
        >
          <title>{props.config.title}</title>
          <path d={props.config.path} />
        </svg>
      </Button>
    </Tooltip>
  )
}

function SocialShareSection(props: {
  post: EditorPost
  selectedLocale: Accessor<string | null>
  publicUrl: string
}) {
  const { LL } = useI18nContext()
  const { user } = useApplication()

  const selectedVersion = createMemo(() => {
    const locale = props.selectedLocale()
    if (!locale) return null
    return props.post.publishedVersions?.find(v => v.locale === locale)
  })

  const shareTitle = createMemo(
    () => selectedVersion()?.metaTitle ?? selectedVersion()?.title ?? "",
  )

  const shareDescription = createMemo(
    () =>
      selectedVersion()?.metaDescription ??
      selectedVersion()?.description ??
      "",
  )

  const shareImage = createMemo(() => selectedVersion()?.imageHeaderUrl ?? "")

  const shareHashtags = createMemo(
    () => props.post.suggestedTags?.join(",") ?? "",
  )

  return (
    <Box gap="8">
      <Text fontSize="s" color="sand700" fontWeight="500">
        {LL().postShare.shareOnSocialMedia()}
      </Text>
      <div class={socialShareGridClass}>
        <For each={socialIcons}>
          {config => (
            <SocialShareButton
              config={config}
              url={`${props.publicUrl}?utm_source=${config.title.toLowerCase()}&utm_campaign=${user.publication?.name}`}
              title={shareTitle()}
              description={shareDescription()}
              media={shareImage()}
              hashtags={shareHashtags()}
            />
          )}
        </For>
      </div>
    </Box>
  )
}
